import { ADS_ATTRIBUTION_OPPREF_COOKIE, deleteCookie, getRawCookie } from "@/lib/cookie";

const ADS_MEASUREMENT_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const ADS_MEASUREMENT_SCRIPT_ID = "openai-ads-measurement-pixel";
const ADS_CONVERSION_CONTEXT_HEADER = "OpenAI-Ads-Conversion-Context";
const ADS_CONVERSION_OPPREF_HEADER = "OpenAI-Ads-Oppref";
const ADS_CONVERSION_SOURCE_URL_HEADER = "OpenAI-Ads-Source-Url";
const OPPREF_QUERY_PARAM = "oppref";
const MAX_OPPREF_LENGTH = 512;

type Oaiq = ((...args: unknown[]) => void) & {
  q?: unknown[][];
};

type AdsMeasurementUser = {
  id: string;
  email: string | null;
};

declare global {
  interface Window {
    oaiq?: Oaiq;
  }
}

let measurementEnabled = false;
let initializedOaiq: Oaiq | undefined;
let initializedPixelId: string | undefined;
let initializedUserSignature: string | undefined;
let pendingUserSignature: string | undefined;

export function setAdsMeasurementEnabled(enabled: boolean) {
  measurementEnabled = enabled;
  if (!enabled && typeof document !== "undefined") {
    deleteCookie(ADS_ATTRIBUTION_OPPREF_COOKIE);
  }
}

export function getAdsConversionRequestHeaders(): Record<string, string> {
  if (!measurementEnabled || typeof window === "undefined") {
    return {};
  }

  const sourceUrl = getConversionSourceUrl();
  if (!sourceUrl) {
    return {};
  }

  const oppref =
    getRawQueryParameter(window.location.search, OPPREF_QUERY_PARAM) ??
    validatedRawOppref(getRawCookie(ADS_ATTRIBUTION_OPPREF_COOKIE));
  return {
    [ADS_CONVERSION_CONTEXT_HEADER]: "web",
    [ADS_CONVERSION_SOURCE_URL_HEADER]: sourceUrl,
    ...(oppref ? { [ADS_CONVERSION_OPPREF_HEADER]: oppref } : {}),
  };
}

export async function initializeAdsMeasurement({
  pixelId,
  debug,
  user,
}: {
  pixelId: string;
  debug: boolean;
  user?: AdsMeasurementUser;
}) {
  if (!measurementEnabled || !pixelId || typeof window === "undefined") {
    return;
  }

  const oaiq = getOrCreateOaiq();
  if (initializedOaiq !== oaiq || initializedPixelId !== pixelId) {
    initializedOaiq = oaiq;
    initializedPixelId = pixelId;
    initializedUserSignature = undefined;
    pendingUserSignature = undefined;
    oaiq("init", {
      pixelId,
      ...(debug ? { debug: true } : {}),
    });
  }

  if (!user?.id) {
    return;
  }

  const normalizedEmail = user.email?.trim().toLowerCase() || null;
  const userSignature = `${user.id}\u0000${normalizedEmail ?? ""}`;
  if (initializedUserSignature === userSignature || pendingUserSignature === userSignature) {
    return;
  }

  pendingUserSignature = userSignature;
  try {
    const [externalIdSha256, emailSha256] = await Promise.all([
      sha256Hex(user.id),
      normalizedEmail ? sha256Hex(normalizedEmail) : Promise.resolve(undefined),
    ]);
    if (!measurementEnabled || window.oaiq !== oaiq) {
      return;
    }

    oaiq("init", {
      user: {
        external_id_sha256: externalIdSha256,
        ...(emailSha256 ? { email_sha256: emailSha256 } : {}),
      },
    });
    initializedUserSignature = userSignature;
  } finally {
    if (pendingUserSignature === userSignature) {
      pendingUserSignature = undefined;
    }
  }
}

export function trackAdsManagerAccountCreated(adAccountId: string) {
  measure(
    "registration_completed",
    { type: "customer_action" },
    { event_id: `registration_completed:${adAccountId}` },
  );
}

export function trackCampaignCreated(campaignId: string) {
  measureCustomEvent("campaign_created", `campaign_created:${campaignId}`);
}

export function trackAdGroupCreated(adGroupId: string) {
  measureCustomEvent("ad_group_created", `ad_group_created:${adGroupId}`);
}

export function trackAdCreated(adId: string) {
  measureCustomEvent("ad_created", `ad_created:${adId}`);
}

function getOrCreateOaiq(): Oaiq {
  if (window.oaiq) {
    return window.oaiq;
  }

  const queuedCalls: unknown[][] = [];
  const queue = ((...args: unknown[]) => {
    queuedCalls.push(args);
  }) as Oaiq;
  queue.q = queuedCalls;
  window.oaiq = queue;

  if (!document.getElementById(ADS_MEASUREMENT_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = ADS_MEASUREMENT_SCRIPT_ID;
    script.async = true;
    script.src = ADS_MEASUREMENT_SDK_URL;
    document.head.appendChild(script);
  }

  return queue;
}

function measureCustomEvent(customEventName: string, eventId: string) {
  measure(
    "custom",
    { type: "custom" },
    {
      custom_event_name: customEventName,
      event_id: eventId,
    },
  );
}

function measure(eventName: string, data: object, options: object) {
  if (!measurementEnabled || typeof window === "undefined" || !window.oaiq) {
    return;
  }

  try {
    window.oaiq("measure", eventName, data, options);
  } catch (error) {
    console.warn("Failed to queue OpenAI Ads measurement event", { eventName, error });
  }
}

function getRawQueryParameter(search: string, name: string): string | null {
  const query = search.startsWith("?") ? search.slice(1) : search;
  for (const pair of query.split("&")) {
    const separatorIndex = pair.indexOf("=");
    const rawName = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
    if (rawName !== name) {
      continue;
    }

    const rawValue = separatorIndex === -1 ? "" : pair.slice(separatorIndex + 1);
    return validatedRawOppref(rawValue);
  }
  return null;
}

function validatedRawOppref(value: string | null | undefined): string | null {
  if (!value || value.length > MAX_OPPREF_LENGTH) {
    return null;
  }
  return value;
}

function getConversionSourceUrl(): string | null {
  try {
    const sourceUrl = new URL(window.location.href);
    if (sourceUrl.protocol !== "https:" && sourceUrl.protocol !== "http:") {
      return null;
    }
    sourceUrl.search = "";
    sourceUrl.hash = "";
    return sourceUrl.toString();
  } catch {
    return null;
  }
}

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
